//core
import React from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { styled } from '@mui/styles';
import Color from 'color';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import * as Ramda from 'ramda';

//mui
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormHelperText,
  Grid as MuiGrid,
  Paper,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { withFormController } from '../../../../../components/hocs/forms';

//Components
import TextFieldBase from '../../../../../components/inputs/text-fields/base/TextFieldBase';
import LanguageIndustryAutoComplete from '../../../../../naics/LanguageIndustryAutoComplete';
import { Show } from '../../../../../components/Show';
import CbButton from '../../../../../components/Buttons/CbButton';
import LanguageForm, {
  useLanguageFormState,
} from '../../../../../i18n/forms/LanguageForm';
import {
  languages,
  type Languages,
} from '../../../../../i18n/i18n.language-config';
import { FormLanguageProvider } from '../../../../../i18n/forms/FormLanguageProvider';
import { withShowable } from '../../../../_global/lib/withShowable';
import {
  getCowbellTranslations,
  useCowbellTranslations,
} from '../../../../../i18n/translations';
import { Flexbox } from '../../../../../components/layout/Flexbox';

interface AdditionalInsuredModalProps {
  data: {
    language: Languages;
    onSubmit: (formValues: FormValues) => void;
    additionalInsureds: AdditionalInsured[];
    readOnly?: boolean;
  };
  close: () => void;
}

const AdditionalInsuredModal = ({
  data: modalProps,
  close,
}: AdditionalInsuredModalProps) => {
  const { gridLayoutSettings, ...languageFormState } = useLanguageFormState({
    language: modalProps.language,
  });
  const { t } = useCowbellTranslations();
  const scrollableRef = React.useRef<null | HTMLDivElement>(null);
  const formMethods = useForm<FormValues>({
    defaultValues: {
      additionalInsureds: modalProps.additionalInsureds,
    },
    resolver: yupResolver(deriveSchema(languageFormState.languageFormSchema)),
  });

  const fieldArrayMethods = useFieldArray({
    control: formMethods.control,
    name: 'additionalInsureds',
  });

  const handleAddAdditionalInsured = ({ scroll }: { scroll: boolean }) => {
    fieldArrayMethods.append({
      businessName: '',
      address1: '',
      address2: '',
      city: '',
      zipCode: '',
      naicsCode: '',
      naicsDescription: '',
    });

    if (scroll) {
      setTimeout(
        () => scrollableRef.current?.scrollIntoView({ behavior: 'smooth' }),
        300
      );
    }
  };

  React.useEffect(() => {
    /* When component first mounts, check if at least one AI exists. If so, dont add a card by default */
    if (formMethods.getValues().additionalInsureds[0]?.businessName) {
      return;
    }
    handleAddAdditionalInsured({ scroll: false });
  }, []);

  return (
    <FormLanguageProvider {...languageFormState}>
      <FormProvider {...formMethods}>
        <form
          noValidate
          onSubmit={formMethods.handleSubmit(
            Ramda.compose(close, modalProps.onSubmit)
          )}
        >
          <DialogContent style={{ padding: '1rem 2.5rem' }}>
            <Show when={!fieldArrayMethods.fields.length}>
              <Box p={4} textAlign="center">
                <Typography variant="body1" color="primary">
                  No Additional Insureds
                </Typography>
              </Box>
            </Show>
            {fieldArrayMethods.fields.map((field, index) => {
              return (
                <AdditionalInsuredCard elevation={3} key={field.id}>
                  <Grid container spacing={2}>
                    <Grid item sm={12}>
                      <TextField
                        required
                        name={`additionalInsureds.${index}.businessName`}
                        label={`${t('organization')} Name`}
                        disabled={modalProps.readOnly}
                      />
                    </Grid>

                    <Grid item sm={6}>
                      <LanguageForm.Address1
                        required
                        name={`additionalInsureds.${index}.address1`}
                        disabled={modalProps.readOnly}
                      />
                    </Grid>
                    <Grid item sm={6}>
                      <LanguageForm.Address2
                        name={`additionalInsureds.${index}.address2`}
                        disabled={modalProps.readOnly}
                      />
                    </Grid>

                    <Grid item sm={12} show={gridLayoutSettings.showAddress3}>
                      <LanguageForm.Address3
                        name={`additionalInsureds.${index}.address3`}
                        disabled={modalProps.readOnly}
                      />
                    </Grid>

                    <Grid item sm={6}>
                      <LanguageForm.City
                        required
                        label="City"
                        name={`additionalInsureds.${index}.city`}
                        disabled={modalProps.readOnly}
                      />
                    </Grid>

                    <Grid item sm={2} show={gridLayoutSettings.showState}>
                      <LanguageForm.State
                        name={`additionalInsureds.${index}.state`}
                        disabled={modalProps.readOnly}
                      />
                    </Grid>

                    <Grid item sm={gridLayoutSettings.showState ? 4 : 6}>
                      <LanguageForm.PostalCode
                        required
                        name={`additionalInsureds.${index}.zipCode`}
                        disabled={modalProps.readOnly}
                      />
                    </Grid>

                    <Grid item sm={12}>
                      <LanguageIndustryAutoComplete
                        name={`additionalInsureds.${index}.naicsCode`}
                        label={t('industry')}
                        required
                        language={languageFormState.language}
                        autoCompleteProps={{
                          defaultValue: getIndustryDefaultValue(
                            formMethods.getValues().additionalInsureds[index]
                          ),
                          disabled: modalProps.readOnly,
                        }}
                        inputLabelBaseProps={{
                          style: { padding: 0 },
                        }}
                        onSelect={(value) => {
                          if (!value?.meta) {
                            return;
                          }

                          formMethods.setValue(
                            `additionalInsureds.${index}.naicsCode`,
                            value.meta.naicsCode,
                            { shouldDirty: true }
                          );

                          if (
                            languageFormState.language === languages['en-GB'] ||
                            languageFormState.language === languages['en-AU']
                          ) {
                            formMethods.setValue(
                              `additionalInsureds.${index}.sicCode`,
                              value.meta.sicCode
                            );

                            formMethods.setValue(
                              `additionalInsureds.${index}.sicDescription`,
                              value.meta.secondaryIndustry
                            );
                          }

                          formMethods.setValue(
                            `additionalInsureds.${index}.naicsDescription`,
                            value.meta.secondaryIndustry
                          );
                        }}
                      />

                      <Show
                        when={
                          !!formMethods.formState?.errors?.additionalInsureds?.[
                            index
                          ]?.naicsCode?.message
                        }
                      >
                        <FormHelperText style={{ marginTop: 0 }}>
                          {(formMethods.formState?.errors?.additionalInsureds?.[
                            index
                          ]?.naicsCode?.message as string) ?? ''}
                        </FormHelperText>
                      </Show>
                    </Grid>
                  </Grid>

                  <Button
                    onClick={() => fieldArrayMethods.remove(index)}
                    className="contrast-text"
                    style={{ position: 'absolute', top: 5, right: 10 }}
                    disabled={modalProps.readOnly}
                  >
                    Remove
                  </Button>
                </AdditionalInsuredCard>
              );
            })}
            <div ref={scrollableRef} />
          </DialogContent>
          <DialogActions>
            <Flexbox>
              <Box>
                <Button
                  startIcon={<AddIcon style={{ pointerEvents: 'none' }} />}
                  className="contrast-text"
                  onClick={() => handleAddAdditionalInsured({ scroll: true })}
                  disabled={modalProps.readOnly}
                >
                  Add Additional Insured
                </Button>
              </Box>

              <Box>
                <CbButton styleName="cancel" onClick={close}>
                  Cancel
                </CbButton>
                <CbButton
                  styleName="ctaButton"
                  type="submit"
                  disabled={modalProps.readOnly}
                >
                  Save
                </CbButton>
              </Box>
            </Flexbox>
          </DialogActions>
        </form>
      </FormProvider>
    </FormLanguageProvider>
  );
};

export default AdditionalInsuredModal;

export const AdditionalInsuredModalConfig = {
  AdditionalInsuredModal: {
    component: AdditionalInsuredModal,
    config: {
      title: 'Additional Insureds',
      fullWidth: true,
      maxWidth: 'md',
    },
  },
};

const TextField = withFormController(TextFieldBase);
const Grid = withShowable(MuiGrid);

const { t } = getCowbellTranslations();

//schema
const deriveSchema = (
  languageFormSchema: Record<string, Yup.Schema<string>>
) => {
  return Yup.object().shape({
    additionalInsureds: Yup.array().of(
      Yup.object().shape({
        businessName: Yup.string()
          .label(`${t('organization')} name`)
          .required(),
        naicsCode: Yup.mixed()
          .label('Industry')
          .test(
            'valid Industry Code',
            '${label} is required',
            (value: string | Industry) => {
              return Boolean(value);
            }
          )
          .required(),
        ...languageFormSchema,
      })
    ),
  });
};

//styling
const AdditionalInsuredCard = styled(Paper)(({ theme }) => {
  return {
    background:
      theme.palette.mode === 'dark'
        ? Color(theme.config.colors.darkGreyActive).darken(0.3).toString()
        : Color(theme.palette.background.active).lighten(0.065).toString(),
    padding: '2rem',
    borderRadius: 5,
    marginBottom: '2rem',
    position: 'relative',
  };
});

const getIndustryDefaultValue = (values: AdditionalInsured) => {
  if (!values.naicsCode) {
    return { label: '', value: '' };
  }

  if (values.sicCode) {
    return {
      label: `${values.sicCode} ${
        values.sicDescription && `[${values.sicDescription}]`
      }`,
      value: values.sicCode,
    };
  }

  return {
    label: `${values.naicsCode} ${
      values.naicsDescription && `[${values.naicsDescription}]`
    }`,
    value: values.naicsCode,
  };
};

export type FormValues = {
  additionalInsureds: AdditionalInsured[];
};

export type AdditionalInsured = {
  businessName: string;
  address1: string;
  address2: string;
  address3?: string;
  city: string;
  state?: string;
  zipCode: string;
  naicsCode: Industry | string;
  naicsDescription: string;
  sicCode?: string;
  sicDescription?: string;
};

type Industry = {
  label: string;
  value: string | number;
  meta: {
    primaryIndustry: string;
    secondaryIndustry: string;
    naicsCode?: string;
  };
};
